/**
 * `useExportConversation` — TanStack mutation wrapping `POST /chat/{id}/export`.
 *
 * The BE responds with a Markdown blob carrying a `Content-Disposition:
 * attachment` header. We don't need to parse the header here — the hook
 * synthesises a fresh `<a download>` with a stable filename rooted in the
 * conversation id so multiple exports during the same session don't collide.
 *
 * Powers the FE slash-command `/export` (see `runSlashCommand` in
 * `routes/_authed/route.tsx`).
 */

import { exportConversation } from "@ando/core/api";
import { useMutation } from "@tanstack/react-query";

/** Best-effort UUID prefix; falls back to the full id when too short. */
function shortId(id: string): string {
  return id.length >= 8 ? id.slice(0, 8) : id;
}

/**
 * Trigger a download of the conversation as a Markdown file. The mutation
 * value (`mutate(conversationId)`) is the conversation id; the file is
 * saved as `ando-conversation-{short-id}.md`.
 */
export function useExportConversation() {
  return useMutation({
    mutationKey: ["chat", "export"],
    mutationFn: (conversationId: string) => exportConversation(conversationId),
    onSuccess: (blob, conversationId) => {
      // Synthesise a `<a download>` — works in every modern browser
      // (Chrome / Firefox / Safari) and Tauri's webview.
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `ando-conversation-${shortId(conversationId)}.md`;
      // Append so Firefox honours the click; remove on next tick.
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    },
  });
}
